import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { Header } from '~/shared/components/Header';
import { Navigation } from '~/shared/components/Navigation';
import { PageTransition } from '~/shared/components/PageTransition';

import Overview from '~/pages/Overview';
import Autocall from '~/pages/Autocall';
import Scheduled from '~/pages/Scheduled';
import PostedOrders from '~/pages/PostedOrders';
import TransferQueue from '~/pages/TransferQueue';
import CallAudit from '~/pages/CallAudit';
import Signin from '~/pages/Signin';
import AuthCallback from '~/pages/AuthCallback';
import AuthLogin from '~/pages/AuthLogin';
import AuthLogout from '~/pages/AuthLogout';

function AuthLoadingScreen() {
  return (
    <div
      className="loading-container"
      style={{
        minHeight: '60vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
      }}
    >
      <div className="spinner" />
      <p style={{ margin: 0, color: 'var(--text-muted)' }}>Loading...</p>
    </div>
  );
}

function ProtectedRoute({ children }: Readonly<{ children: React.ReactNode }>) {
  const isAuthenticated = useIsAuthenticated();
  const { accounts, inProgress } = useMsal();
  const location = useLocation();

  if (inProgress !== InteractionStatus.None) {
    return <AuthLoadingScreen />;
  }

  if (!isAuthenticated || accounts.length === 0) {
    const currentPath = location.pathname + location.search;
    const signinPath = currentPath
      ? `/signin?redirectTo=${encodeURIComponent(currentPath)}`
      : '/signin';
    return <Navigate to={signinPath} replace state={{ from: location }} />;
  }

  return (
    <>
      <Header user={accounts[0]} />
      <Navigation />
      <main>
        <PageTransition>{children}</PageTransition>
      </main>
    </>
  );
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/signin" element={<Signin />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/login" element={<AuthLogin />} />
      <Route path="/auth/logout" element={<AuthLogout />} />

      <Route path="/" element={<ProtectedRoute><Overview /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Overview /></ProtectedRoute>} />
      <Route path="/autocall" element={<ProtectedRoute><Autocall /></ProtectedRoute>} />
      <Route path="/scheduled" element={<ProtectedRoute><Scheduled /></ProtectedRoute>} />
      <Route path="/posted-order" element={<ProtectedRoute><PostedOrders /></ProtectedRoute>} />
      <Route path="/call-audit" element={<ProtectedRoute><CallAudit /></ProtectedRoute>} />
      <Route path="/transfer-queue" element={<ProtectedRoute><TransferQueue /></ProtectedRoute>} />
    </Routes>
  );
}
